import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { directAxiosInstance } from "@lib/api"
import { getAssetPath, excludeCorporateRedirection, Validate } from "@commonUtils"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import Button from "@components/Button/Button"
import css from "../Auth.scss"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { setConflictCaseInfo, setDefault } from "../reducer"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import { enterEmailNonConflictGA, enterNewEmailConflictGA } from "../GAEvents"
import { useDispatch, useSelector } from "react-redux"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

const EmailConflict = ({ forwardHandler, onSuccess, state, localDispatch }) => {
    const { info, conflictInfo } = state
    const dispatch = useDispatch()
    const { authConfig } = useSelector((state) => state.shellReducer)
    const [email, setEmail] = useState(info.email || conflictInfo.conflictEmail || "")
    const [error, setError] = useState(null)
    const [ctaLabel, setCtaLabel] = useState("Continue")
    const [iconUrl, setIconUrl] = useState(null)
    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    /**
     * @description when user come on this screen cursor should be on inputField .
     */
    const inputRef = useRef(null)
    useEffect(() => {
        setFocused(true)
        inputRef.current.focus()
    }, [])
    const [focused, setFocused] = useState(false)
    const onFocus = () => {
        setFocused(true)
    }

    const onBlur = () => {
        if (email === "") {
            setFocused(false)
        }
    }
    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSubmit()
        }
    }
    /**
     * @description Function to check valid input
     */
    const errorCheck = () => {
        setError(null)

        const errs = Validate({
            email: email,
        })
        setError(errs)
        return errs
    }

    /**
     * @description Function to resolve email conflict based on user input or complete the login flow if the email ID
     *              is missing for the same phone number
     */
    const handleSubmit = () => {
        const errs = errorCheck()

        if (!errs && !active) {
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))

            directAxiosInstance()
                .post(
                    conflictInfo.conflict
                        ? "auth/phone/create_token_conflict_resolve"
                        : "auth/phone/complete",
                    {
                        number: info.phone,
                        email: email,
                    }
                )
                .then((res) => {
                    if (res && res.data) {
                        const userData = res.data && res.data.user_data ? res.data.user_data : null
                        const userFlags = res.data && res.data.user_flags ? res.data.user_flags : null
                        if (userData) {
                            const corporateRedirectionUrl = excludeCorporateRedirection()
                            info.existingUser
                                ? enterEmailNonConflictGA("Login", "Success")
                                : enterEmailNonConflictGA("Signup", "Success")
                            onSuccess(userData, corporateRedirectionUrl)
                        } else {
                            if (userFlags.is_otp_required) {
                                enterNewEmailConflictGA("Success")
                                localDispatch(setConflictCaseInfo({ conflictEmail: email }))
                                forwardHandler("OtpConflict")
                            }
                        }
                    }
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    setActive(false)
                })
                .catch((err) => {
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    const errMsg = err?.data?.error?.message || "Server error"
                    setError({ email: errMsg })
                    setActive(false)
                    conflictInfo.conflict
                        ? enterNewEmailConflictGA("Error")
                        : info.existingUser
                        ? enterEmailNonConflictGA("Login", "Error")
                        : enterEmailNonConflictGA("Signup", "Error")
                })
        }
    }

    return (
        <>
            <div className={css.rightContainer}>
                {!authConfig.restrictUserFromClosingAuth && (
                    <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                        <div>
                            <ARIAButton
                                onClick={() => {
                                    dispatch(setDefaultAuthConfig())
                                    localDispatch(setDefault())
                                }}
                                ariaLabel={"Close email conflict popup"}
                            >
                                <img
                                    src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                    alt={"Close icon"}
                                />
                            </ARIAButton>
                        </div>
                    </div>
                )}
                <div className={css.authContainer}>
                    <div className={`${css.paddingAuth} flex flexColumn justifyBetween`}>
                        <div>
                            <span className={css.heading}>Enter your Email id</span>
                            <div className={`smallRegular textSecondary ${css.subHeadingMargin}`}>
                                We are almost ready! Just need your email id to keep your account extra safe
                            </div>

                            <div className={css.marginBottom16}>
                                <InputField
                                    name="email"
                                    label="Enter your email id"
                                    type="text"
                                    value={email}
                                    onChange={setEmail}
                                    onFocus={() => onFocus()}
                                    onBlur={() => onBlur()}
                                    alertType={"error"}
                                    alertMessage={error && error.email}
                                    focused={focused}
                                    borderType={"primary"}
                                    onKeyUp={(e) => onKeyUp(e)}
                                    ref={inputRef}
                                />
                            </div>
                        </div>
                        <div className="flexColumn alignCenter ">
                            <Button
                                label={!iconUrl ? ctaLabel : null}
                                iconUrl={iconUrl}
                                isDisabled={iconUrl ? true : false}
                                onClick={() => handleSubmit()}
                            />
                            <div className="marginTop-16 flexColumn alignCenter">
                                <div className={`${css.footerSubText} flexColumn alignCenter`}>
                                    <div className="textTertiary">
                                        By signing up, you agree to our
                                        <div>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/tnc"
                                                }}
                                            >
                                                Terms and Conditions
                                            </span>
                                            <span className={css.marginBoth4}>&</span>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/PrivacyPolicy"
                                                }}
                                            >
                                                Privacy policy
                                            </span>
                                        </div>
                                    </div>
                                    <ConditionalLink
                                        className={` ${css.marginFooter} smallSemiBold textAction ${css.cursorPointer}`}
                                        onClick={() => forwardHandler("NeedHelp")}
                                    >
                                        <span>Need Help?</span>
                                        <span className={css.marginLeft4}> Get in touch</span>
                                    </ConditionalLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

EmailConflict.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    forwardHandler: PropTypes.func.isRequired,
    state: PropTypes.shape({
        info: PropTypes.shape({
            email: PropTypes.string,
            existingUser: PropTypes.bool,
            phone: PropTypes.string,
        }),
        conflictInfo: PropTypes.shape({
            conflict: PropTypes.bool,
            conflictEmail: PropTypes.string,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default EmailConflict
